import ErrorDialog from "@/components/ErrorDialog"
import MediaFrame from "@/components/MediaFrame"
import VideoCoverPicker from "@/components/VideoCoverPicker"
import { useAuth } from "@/hooks/useAuth"
import { useAuthModal } from "@/hooks/useAuthModal"
import { CITY_KEY, DRAFT_KEY, LIBRARY_BEDS, extractHashtags, todayPrompt, topicFromCaption } from "@/lib/culture"
import { PHOTO_FILTERS, postPath } from "@/lib/filters"
import { lumi } from "@/lib/lumi"
import { fetchPostById } from "@/lib/posts"
import { privacyUpdatePost } from "@/lib/privacy"
import { notify } from "@/lib/social"
import { uploadFile } from "@/lib/upload"
import { coverFileFromVideo, waitForVideoData } from "@/lib/videoCover"
import type { Post } from "@/types"
import { useEffect, useMemo, useRef, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"

const ACCEPT = "image/*,video/*,.mp4,.mov,.webm,.m4v,.3gp"

export default function CreatePage() {
  const { user, isAuthenticated } = useAuth()
  const { openAuth } = useAuthModal()
  const [params] = useSearchParams()
  const remixId = params.get("remix") || ""
  const fileRef = useRef<HTMLInputElement>(null)
  const cameraRef = useRef<HTMLInputElement>(null)
  const playerRef = useRef<HTMLVideoElement>(null)
  const coverRef = useRef<HTMLVideoElement>(null)
  const [step, setStep] = useState<"pick" | "edit" | "done">("pick")
  const [file, setFile] = useState<File | null>(null)
  const [localUrl, setLocalUrl] = useState("")
  const [isVideo, setIsVideo] = useState(false)
  const [duration, setDuration] = useState(0)
  const [trimStart, setTrimStart] = useState(0)
  const [trimEnd, setTrimEnd] = useState(20)
  const [coverTime, setCoverTime] = useState(0)
  const [caption, setCaption] = useState("")
  const [city, setCity] = useState("")
  const [audioTitle, setAudioTitle] = useState("Original audio")
  const [audioMode, setAudioMode] = useState("original")
  const [filter, setFilter] = useState("normal")
  const [scheduledAt, setScheduledAt] = useState("")
  const [remixOf, setRemixOf] = useState<Post | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState("")
  const [postedId, setPostedId] = useState("")
  const prompt = useMemo(() => todayPrompt(), [])

  useEffect(() => {
    const storedCity = localStorage.getItem(CITY_KEY) || ""
    const raw = localStorage.getItem(DRAFT_KEY)
    if (!raw) {
      if (storedCity) setCity(storedCity)
      return
    }
    try {
      const draft = JSON.parse(raw) as { caption?: string; city?: string }
      setCaption(draft.caption || "")
      setCity(draft.city || storedCity)
    } catch {
      if (storedCity) setCity(storedCity)
    }
  }, [])

  useEffect(() => {
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ caption, city, scheduledAt, audioTitle }))
  }, [caption, city, scheduledAt, audioTitle])

  useEffect(() => {
    if (!remixId) return
    fetchPostById(remixId).then((post) => {
      setRemixOf(post)
      if (post?.caption) setCaption(`Remix with @original — ${post.caption}`)
    }).catch((err) => console.error("Remix load failed:", err))
  }, [remixId])

  useEffect(() => () => {
    if (localUrl) URL.revokeObjectURL(localUrl)
  }, [localUrl])


  const pickFile = async (next?: File | null, fromCamera = false) => {
    if (!next) return
    if (next.size > 10 * 1024 * 1024) {
      setError("Keep files under 10MB.")
      return
    }
    const video = next.type.startsWith("video/") || /\.(mp4|mov|webm|m4v|3gp)$/i.test(next.name)
    const url = URL.createObjectURL(next)
    setFile(next)
    setLocalUrl(url)
    setIsVideo(video)
    setCoverTime(0)
    setStep("edit")
    if (video) {
      window.setTimeout(async () => {
        const el = playerRef.current
        if (!el) return
        try {
          await waitForVideoData(el)
          const len = Math.min(el.duration || 20, 60)
          setDuration(len)
          setTrimEnd(Math.min(Math.max(8, len), 20))
        } catch (err) {
          console.error("Video inspect failed:", err)
        }
      }, 50)
    }
    void fromCamera
  }

  const publish = async (asDraft = false) => {
    if (!isAuthenticated || !user?.userId) {
      openAuth()
      return
    }
    if (!file) {
      setError("Pick a clip first.")
      return
    }
    setBusy(true)
    try {
      const mediaUrl = await uploadFile(file)
      let imageUrl = isVideo ? "" : mediaUrl
      if (isVideo) {
        try {
          const coverEl = coverRef.current || playerRef.current
          if (coverEl) {
            const cover = await coverFileFromVideo(coverEl, coverTime)
            imageUrl = await uploadFile(cover)
          }
        } catch (err) {
          console.error("Cover capture failed:", err)
        }
      }
      const now = new Date().toISOString()
      const status = asDraft ? "draft" : scheduledAt ? "scheduled" : "published"
      const credit = remixOf ? ` remix @${remixOf.authorId.slice(0, 6)}` : ""
      const created = await lumi.entities.posts.create({
        authorId: user.userId,
        ownerId: user.userId,
        ownerWallet: "",
        imageUrl,
        videoUrl: isVideo ? mediaUrl : "",
        mediaType: isVideo ? "video" : "image",
        caption: `${caption.trim()}${credit}`.trim() || prompt,
        filter,
        brightness: 100,
        contrast: 100,
        saturate: 100,
        priceEth: 0,
        forSale: false,
        walletAddress: "",
        sold: false,
        onChain: false,
        chainStatus: "none",
        city: city.trim(),
        topic: topicFromCaption(caption, "cities"),
        audioTitle,
        audioMode,
        onScreenCaption: "",
        hashtags: extractHashtags(caption),
        status,
        scheduledAt: scheduledAt ? new Date(scheduledAt).toISOString() : "",
        remixOfPostId: remixOf?._id || "",
        remixCount: 0,
        boostedUntil: "",
        pinned: false,
        trimStart,
        trimEnd,
        coverTime,
        hiddenBy: "",
        creator: user.userId,
        createdAt: now,
        updatedAt: now,
      }) as Post
      if (remixOf?._id) {
        await privacyUpdatePost(remixOf._id, "remix")
        await notify({ recipientId: remixOf.authorId, actorId: user.userId, type: "remix", postId: remixOf._id })
      }
      localStorage.removeItem(DRAFT_KEY)
      setPostedId(created?._id || "")
      setStep("done")
    } catch (err) {
      console.error("Publish failed:", err)
      setError((err as Error)?.message || "Could not post.")
    } finally {
      setBusy(false)
    }
  }

  if (step === "done") {
    const url = `${window.location.origin}${postPath(postedId)}`
    return (
      <div className="mx-auto max-w-xl px-4 py-8 text-center">
        <h1 className="font-[Syne] text-3xl font-bold">Posted</h1>
        <p className="mt-2 text-sm text-[#8A8A8A]">Share the clip or shoot another.</p>
        <div className="mt-6 grid gap-2">
          <button type="button" onClick={() => navigator.clipboard.writeText(url)} className="rounded-xl bg-[#00FF66] py-3 font-semibold text-black">Copy link</button>
          <button type="button" onClick={() => navigator.share?.({ title: "KLIPO", url }).catch(() => undefined)} className="rounded-xl border border-[#1F1F1F] py-3">Native share</button>
          <button type="button" onClick={() => { setStep("pick"); setFile(null); setLocalUrl(""); setPostedId("") }} className="rounded-xl border border-[#1F1F1F] py-3">Post another</button>
        </div>
      </div>
    )
  }

  if (step === "pick") {
    return (
      <div className="mx-auto max-w-xl px-4 py-6">
        <p className="text-sm font-semibold text-[#00FF66]">{prompt}</p>
        <h1 className="mt-2 font-[Syne] text-3xl font-bold">New clip</h1>
        {remixOf ? <p className="mt-2 text-sm text-[#8A8A8A]">Remixing a clip. Original stays pinned on top.</p> : null}
        <div className="mt-6 grid gap-3">
          <button type="button" onClick={() => cameraRef.current?.click()} className="rounded-2xl bg-[#00FF66] py-4 text-lg font-semibold text-black">Record</button>
          <button type="button" onClick={() => fileRef.current?.click()} className="rounded-2xl border border-[#1F1F1F] bg-[#0B0B0B] py-4">Camera roll</button>
          {remixId ? <p className="text-center text-xs text-[#8A8A8A]">Remix this clip is already selected.</p> : null}
        </div>
        <input ref={fileRef} type="file" accept={ACCEPT} hidden onChange={(event) => pickFile(event.target.files?.[0])} />
        <input ref={cameraRef} type="file" accept={ACCEPT} capture="environment" hidden onChange={(event) => pickFile(event.target.files?.[0], true)} />
        {error ? <ErrorDialog message={error} onClose={() => setError("")} /> : null}
      </div>
    )
  }

  const previewPost = {
    _id: "preview",
    authorId: user?.userId || "",
    imageUrl: isVideo ? "" : localUrl,
    videoUrl: isVideo ? localUrl : "",
    mediaType: isVideo ? "video" : "image",
    caption,
    filter,
    brightness: 100,
    contrast: 100,
    saturate: 100,
    priceEth: 0,
    forSale: false,
    walletAddress: "",
    sold: false,
    coverTime,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  } as Post

  return (
    <div className="mx-auto max-w-2xl px-4 pb-8 pt-3">
      {remixOf ? (
        <div className="mb-3 overflow-hidden rounded-xl border border-[#1F1F1F]">
          <div className="aspect-video max-h-28 bg-black">
            <MediaFrame post={remixOf} preview autoPlay={false} />
          </div>
          <p className="px-3 py-2 text-xs text-[#8A8A8A]">Original on top · your clip below</p>
        </div>
      ) : null}
      <div className="mx-auto flex aspect-[9/16] max-h-[52vh] items-center justify-center overflow-hidden rounded-2xl bg-black">
        <MediaFrame post={previewPost} autoPlay playerRef={playerRef} muted={audioMode === "mute"} />
      </div>
      {isVideo ? (
        <div className="mt-3 space-y-2 text-xs text-[#8A8A8A]">
          <label className="block">Trim start {trimStart.toFixed(1)}s
            <input type="range" min={0} max={Math.max(duration - 1, 1)} step={0.1} value={trimStart} onChange={(event) => setTrimStart(Number(event.target.value))} className="w-full" />
          </label>
          <label className="block">Trim end {trimEnd.toFixed(1)}s
            <input type="range" min={trimStart + 1} max={Math.max(duration, 2)} step={0.1} value={trimEnd} onChange={(event) => setTrimEnd(Number(event.target.value))} className="w-full" />
          </label>
          <VideoCoverPicker src={localUrl} initialTime={coverTime} videoRef={coverRef} onTimeChange={setCoverTime} />
          <p>Max 60s. Aim for 8–20s.</p>
        </div>
      ) : null}
      <p className="mt-4 text-sm font-semibold text-[#00FF66]">{prompt}</p>
      <textarea value={caption} onChange={(event) => setCaption(event.target.value)} rows={3} placeholder="Caption with @mentions and #hashtags" className="mt-2 w-full rounded-xl border border-[#1F1F1F] bg-[#0B0B0B] px-3 py-2 text-sm outline-none" />
      <input value={city} onChange={(event) => setCity(event.target.value)} className="mt-2 w-full rounded-xl border border-[#1F1F1F] bg-[#0B0B0B] px-3 py-2 text-sm outline-none" placeholder="Location (optional)" />
      <div className="mt-3 flex flex-wrap gap-2">
        {LIBRARY_BEDS.map((bed) => (
          <button key={bed} type="button" onClick={() => { setAudioTitle(bed === "Mute" ? "Mute" : bed); setAudioMode(bed === "Mute" ? "mute" : bed === "Original audio" ? "original" : "library") }} className={`rounded-full px-3 py-1 text-xs ${audioTitle === bed ? "bg-[#00FF66] text-black" : "bg-[#141414]"}`}>{bed}</button>
        ))}
      </div>
      <div className="mt-3 flex gap-2 overflow-x-auto">
        {PHOTO_FILTERS.map((item) => (
          <button key={item.id} type="button" onClick={() => setFilter(item.id)} className={`shrink-0 rounded-full px-3 py-1 text-xs ${filter === item.id ? "bg-[#00FF66] text-black" : "bg-[#141414]"}`}>{item.label}</button>
        ))}
      </div>
      <label className="mt-3 block text-xs text-[#8A8A8A]">
        Schedule
        <input type="datetime-local" value={scheduledAt} onChange={(event) => setScheduledAt(event.target.value)} className="mt-1 w-full rounded-xl border border-[#1F1F1F] bg-[#0B0B0B] px-3 py-2 text-sm text-white" />
      </label>
      <div className="mt-4 grid grid-cols-2 gap-2">
        <button type="button" disabled={busy} onClick={() => publish(true)} className="rounded-xl border border-[#1F1F1F] py-3 text-sm">Save draft</button>
        <button type="button" disabled={busy} onClick={() => publish(false)} className="rounded-xl bg-[#00FF66] py-3 text-sm font-semibold text-black">{busy ? "Posting..." : "Post"}</button>
      </div>
      <Link to="/" className="mt-3 block text-center text-xs text-[#8A8A8A]">Back to Home</Link>
      {error ? <ErrorDialog message={error} onClose={() => setError("")} /> : null}
    </div>
  )
}
